// The requester-facing request API (architecture plan §11.2; build plan P4): the strict intake
// body, the follow-up validation per category, the list and detail shapes, and the mutation
// bodies. Strict objects: a requester cannot set priority, assignment, or a staff-only state
// through any route because the schema has no such key (P4 exit criteria).

import { z } from "zod";
import { getCategory, type RequestCategory } from "./categories";
import { RequestStatusSchema } from "./enums";
import { RequestFormFieldsSchema } from "./intake";
import { PublicIdSchema } from "./public-id";

export const IDEMPOTENCY_KEY_HEADER = "idempotency-key";
export const LIST_DEFAULT_LIMIT = 25;
export const LIST_MAX_LIMIT = 100;

/** Issues the follow-up answers raise against the category's field definitions. */
export function validateFollowUpAnswers(
  category: RequestCategory,
  answers: Record<string, unknown>,
): Array<{ path: string; message: string }> {
  const issues: Array<{ path: string; message: string }> = [];
  const known = new Set(category.followUpFields.map((f) => f.id));
  for (const key of Object.keys(answers)) {
    if (!known.has(key)) issues.push({ path: key, message: "not a field of this category" });
  }
  for (const field of category.followUpFields) {
    const value = answers[field.id];
    const present = value !== undefined && value !== null && value !== "";
    if (!present) {
      if (field.required) issues.push({ path: field.id, message: "required" });
      continue;
    }
    switch (field.type) {
      case "boolean":
        if (typeof value !== "boolean")
          issues.push({ path: field.id, message: "expected true or false" });
        break;
      case "select":
        if (typeof value !== "string" || !field.options?.includes(value)) {
          issues.push({ path: field.id, message: "not one of the options" });
        }
        break;
      case "date":
        if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          issues.push({ path: field.id, message: "expected a date as YYYY-MM-DD" });
        }
        break;
      default:
        if (typeof value !== "string" || value.length > 4000) {
          issues.push({ path: field.id, message: "expected text of at most 4000 characters" });
        }
    }
  }
  return issues;
}

export const CreateRequestBodySchema = RequestFormFieldsSchema.extend({
  subject: z.string().trim().min(3).max(200),
  description: z.string().trim().min(10).max(20000),
  department: z.string().trim().min(1).max(200),
  location: z.string().trim().min(1).max(200),
  callback: z.string().trim().min(3).max(60),
  affectedSystemOrDevice: z.string().trim().max(500).default(""),
  onset: z.string().trim().max(200).default(""),
  errorText: z.string().max(20000).default(""),
  troubleshootingAttempted: z.string().max(20000).default(""),
  neededBy: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .default(null),
  sponsor: z.string().trim().max(200).nullable().default(null),
  sensitivityAcknowledged: z.literal(true, {
    error: "the data-sensitivity acknowledgement is required",
  }),
})
  .strict()
  .superRefine((body, ctx) => {
    const category = getCategory(body.category);
    if (!category) {
      ctx.addIssue({ code: "custom", path: ["category"], message: "unknown category" });
      return;
    }
    if (!category.portalReceivable) {
      // §7.7: account-access classes arrive by phone; staff raise them on the caller's behalf.
      ctx.addIssue({
        code: "custom",
        path: ["category"],
        message: `${category.label} cannot be raised through the portal; call the support number`,
      });
      return;
    }
    for (const issue of validateFollowUpAnswers(category, body.followUpAnswers)) {
      ctx.addIssue({
        code: "custom",
        path: ["followUpAnswers", issue.path],
        message: issue.message,
      });
    }
  });
export type CreateRequestBody = z.infer<typeof CreateRequestBodySchema>;
export type CreateRequestBodyInput = z.input<typeof CreateRequestBodySchema>;

export const CreateRequestResponseSchema = z.object({
  id: z.uuid(),
  publicId: PublicIdSchema,
  status: RequestStatusSchema,
  /** §7.2: visibly not yet on the board. */
  onBoard: z.literal(false),
  /** True when a medium-confidence value was redacted (§13.5); the requester is told. */
  redacted: z.boolean(),
});
export type CreateRequestResponse = z.infer<typeof CreateRequestResponseSchema>;

/** The refusal for a high-confidence credential. Guidance, rule ids, never the value. */
export const IntakeRejectionSchema = z.object({
  error: z.object({
    code: z.literal("credential_detected"),
    message: z.string(),
    rules: z.array(z.string()),
    requestId: z.string().optional(),
  }),
});

export const RequestSummarySchema = z.object({
  id: z.uuid(),
  publicId: PublicIdSchema,
  subject: z.string(),
  category: z.string(),
  status: RequestStatusSchema,
  onBoard: z.boolean(),
  workItemId: z.number().int().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type RequestSummary = z.infer<typeof RequestSummarySchema>;

export const ThreadMessageSchema = z.object({
  id: z.uuid(),
  authorKind: z.enum(["requester", "staff", "system", "agent"]),
  body: z.string(),
  createdAt: z.iso.datetime(),
});
export type ThreadMessage = z.infer<typeof ThreadMessageSchema>;

export const RequestDetailSchema = RequestSummarySchema.extend({
  description: z.string(),
  department: z.string(),
  location: z.string(),
  onsiteOrRemote: z.enum(["onsite", "remote"]),
  callback: z.string(),
  affectedSystemOrDevice: z.string(),
  onset: z.string(),
  errorText: z.string(),
  troubleshootingAttempted: z.string(),
  businessImpact: z.enum(["individual", "multiple_people", "company_wide"]),
  neededBy: z.string().nullable(),
  sponsor: z.string().nullable(),
  followUpAnswers: z.record(z.string(), z.unknown()),
  secretRedacted: z.boolean(),
  withdrawnAt: z.iso.datetime().nullable(),
  messages: z.array(ThreadMessageSchema),
});
export type RequestDetail = z.infer<typeof RequestDetailSchema>;

export const RequestListSchema = z.object({
  items: z.array(RequestSummarySchema),
  nextCursor: z.string().nullable(),
});
export type RequestList = z.infer<typeof RequestListSchema>;

export const CreateMessageBodySchema = z
  .object({ body: z.string().trim().min(1).max(20000) })
  .strict();
export type CreateMessageBody = z.infer<typeof CreateMessageBodySchema>;

export const WithdrawBodySchema = z
  .object({ reason: z.string().trim().max(2000).optional() })
  .strict();
export type WithdrawBody = z.infer<typeof WithdrawBodySchema>;

export const MessageCreatedSchema = z.object({
  id: z.uuid(),
  status: RequestStatusSchema,
  reopened: z.boolean(),
  redacted: z.boolean(),
});
